import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Check, Loader2, Info, ExternalLink } from 'lucide-react';
import { formatMoney } from '../../lib/format.js';

const EASE = [0.2, 0.7, 0.2, 1];

/**
 * A copy button that is the whole row.
 *
 * An account number typed by hand is an account number typed wrongly, and unlike
 * a card there is no gateway between the customer and a real bank transfer to
 * catch it. Making the value itself the control is the point: the customer never
 * has to select text on a phone.
 */
function DetailRow({ label, value, mono = true }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  // A component unmounted mid-timeout must not set state afterwards.
  useEffect(() => () => clearTimeout(timer.current), []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard denied — the value is on screen and readable either way */
    }
  };

  return (
    <div className="flex items-center justify-between gap-3 px-3.5 py-2.5">
      <dt className="shrink-0 text-xs text-muted">{label}</dt>
      <dd className="flex min-w-0 items-center gap-1.5">
        <span className={`truncate text-sm text-ink ${mono ? 'font-mono' : ''}`}>{value}</span>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 rounded-md p-1 text-faint transition-colors hover:bg-surface-2 hover:text-ink"
          aria-label={copied ? `${label} copied` : `Copy ${label}`}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </dd>
    </div>
  );
}

/**
 * The UPI QR, rendered from the intent URI the *server* built.
 *
 * The encoder is imported only when this branch runs, so a customer paying by
 * card never downloads it.
 */
function UpiQr({ uri }) {
  const [dataUrl, setDataUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!uri) return undefined;
    let cancelled = false;

    import('qrcode')
      .then((QR) => QR.toDataURL(uri, { margin: 1, width: 440, errorCorrectionLevel: 'M' }))
      .then((url) => { if (!cancelled) setDataUrl(url); })
      // A QR that will not render must not hide the VPA, which is the fallback.
      .catch(() => { if (!cancelled) setFailed(true); });

    return () => { cancelled = true; };
  }, [uri]);

  if (failed || !uri) return null;

  return (
    <div className="mb-4 flex justify-center">
      <div className="rounded-xl border bg-white p-3" style={{ borderColor: 'rgb(var(--line))' }}>
        {dataUrl ? (
          <motion.img
            src={dataUrl}
            alt="UPI QR code"
            width={176}
            height={176}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.28, ease: EASE }}
            className="h-44 w-44"
          />
        ) : (
          <div className="grid h-44 w-44 place-items-center">
            <Loader2 className="h-5 w-5 animate-spin text-faint" aria-hidden="true" />
          </div>
        )}
      </div>
    </div>
  );
}

/** Which fields each method shows, and in what order. */
const ROWS = {
  bank_transfer: [
    ['accountName', 'Account name', false],
    ['accountNumber', 'Account number', true],
    ['ifsc', 'IFSC / routing', true],
    ['bankName', 'Bank', false],
    ['swift', 'SWIFT / BIC', true],
  ],
  upi: [
    ['vpa', 'UPI ID', true],
    ['payeeName', 'Payee', false],
  ],
  cheque: [['payableTo', 'Payable to', false]],
  cash: [],
};

/**
 * HC-10, offline half: the outcome of choosing one of these is never "paid". It
 * is "we are waiting for your money", which is the honest answer — no processor
 * is watching, so a human has to confirm it arrived.
 *
 * Everything the customer needs to actually send the money is on screen *before*
 * they confirm, because once they confirm the session closes.
 */
export function OfflineInstructions({ method, payment, accent, onConfirm, submitting, reference }) {
  const rows = (ROWS[method.key] ?? []).filter(([key]) => method.details?.[key]);
  const isUpi = method.key === 'upi';
  const isCash = method.key === 'cash';

  // A phone can hand the intent URI straight to a UPI app; a desktop cannot.
  const onMobile = typeof navigator !== 'undefined' && /android|iphone|ipad/i.test(navigator.userAgent);

  return (
    <motion.div
      initial={{ y: 8 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.3, ease: EASE }}
    >
      <h2 className="text-base font-semibold tracking-tight text-ink">
        {isCash ? `Pay ${formatMoney(payment.amount, payment.currency)} in cash` : `Pay by ${method.label.toLowerCase()}`}
      </h2>
      <p className="mb-5 mt-1 text-sm leading-relaxed text-muted">
        {isCash
          ? 'No payment is taken now.'
          : `Send exactly ${formatMoney(payment.amount, payment.currency)} using the details below.`}
      </p>

      {isUpi && <UpiQr uri={method.upiUri} />}

      {rows.length > 0 && (
        <dl
          className="mb-4 divide-y overflow-hidden rounded-xl border"
          style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface))' }}
        >
          {rows.map(([key, label, mono]) => (
            <DetailRow key={key} label={label} value={method.details[key]} mono={mono} />
          ))}
          {/*
            * The reference is what lets the merchant match the money to this
            * order. Without it a bank transfer is an anonymous credit, and
            * confirming it becomes guesswork.
            */}
          {reference && <DetailRow label="Reference" value={reference} />}
        </dl>
      )}

      {isUpi && onMobile && method.upiUri && (
        <a
          href={method.upiUri}
          className="mb-4 flex h-11 w-full items-center justify-center gap-2 rounded-lg border text-sm font-medium text-ink transition-colors hover:bg-surface-2"
          style={{ borderColor: 'rgb(var(--line))' }}
        >
          <ExternalLink className="h-4 w-4" aria-hidden="true" />
          Open your UPI app
        </a>
      )}

      {method.instructions && (
        <div
          className="mb-4 flex items-start gap-2.5 rounded-xl border px-3.5 py-3"
          style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface-2))' }}
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-faint" aria-hidden="true" />
          <p className="whitespace-pre-line text-sm leading-relaxed text-ink-soft">{method.instructions}</p>
        </div>
      )}

      <motion.button
        type="button"
        onClick={onConfirm}
        disabled={submitting}
        aria-busy={submitting || undefined}
        whileTap={submitting ? {} : { scale: 0.985 }}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-lg text-[15px] font-semibold text-white shadow-sm transition-[filter,opacity] hover:brightness-[0.94] disabled:opacity-80"
        style={{ background: accent }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {submitting ? (
            <motion.span
              key="busy"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
              className="flex items-center gap-2"
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Saving…
            </motion.span>
          ) : (
            <motion.span
              key="idle"
              initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
            >
              {/* Never "I have paid" for cash — they have not, and will not until
                  they are handed the goods. */}
              {isCash ? 'Confirm this order' : 'I’ve sent the payment'}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <p className="mt-3 text-center text-xs leading-relaxed text-muted">
        {isCash
          ? 'The merchant will confirm once you have paid.'
          : 'The merchant confirms this once the money reaches them.'}
      </p>
    </motion.div>
  );
}

/**
 * The same details again on the outcome screen.
 *
 * Repetition is deliberate: the customer has confirmed they will pay but has not
 * paid yet, and closing this tab is how they lose the account number. This is
 * the screen they will still have open when they open their banking app.
 */
export function OfflineRecap({ method, reference }) {
  const rows = (ROWS[method.key] ?? []).filter(([key]) => method.details?.[key]);
  if (rows.length === 0 && !reference) return null;

  return (
    <dl
      className="divide-y overflow-hidden rounded-xl border"
      style={{ borderColor: 'rgb(var(--line))', background: 'rgb(var(--surface))' }}
    >
      {rows.map(([key, label, mono]) => (
        <DetailRow key={key} label={label} value={method.details[key]} mono={mono} />
      ))}
      {reference && <DetailRow label="Reference" value={reference} />}
    </dl>
  );
}

export default OfflineInstructions;
